import React, { useMemo } from 'react';
import {
  SimulationHotspotInteraction,
  SimulationSceneInteraction,
} from '../../../types/v2';
import './MsiBiosNavigationScene.css';

interface MsiBiosNavigationSceneProps {
  renderState: Record<string, any>;
  interactions?: SimulationSceneInteraction[];
  disabled?: boolean;
  onAction: (actionId: string) => void;
}

export const MsiBiosNavigationScene: React.FC<MsiBiosNavigationSceneProps> = ({
  renderState,
  interactions = [],
  disabled = false,
  onAction,
}) => {
  const screen = String(renderState.screen || 'startup');
  const biosPage = String(renderState.bios_page || 'overview');
  const checks = {
    cpu: renderState.cpu_checked === true,
    memory: renderState.memory_checked === true,
    nvme: renderState.nvme_checked === true,
    boot: renderState.boot_checked === true,
  };
  const hotspots = useMemo(() => (
    Object.fromEntries(
      interactions
        .filter((item): item is SimulationHotspotInteraction => item.kind === 'hotspot')
        .map((item) => [item.id, item]),
    )
  ), [interactions]);

  const run = (id: string) => {
    const interaction = hotspots[id];
    if (!interaction || disabled) return;
    onAction(interaction.action_id);
  };

  const control = (
    id: string,
    label: string,
    className = 'bios-sim-button',
    pressed?: boolean,
  ) => (
    hotspots[id] ? (
      <button
        type="button"
        className={className}
        onClick={() => run(id)}
        disabled={disabled}
        aria-label={hotspots[id].label}
        aria-pressed={pressed}
      >
        {label}
      </button>
    ) : null
  );

  return (
    <div className="bios-sim-shell" aria-label="MSI BIOS navigation training simulation">
      <div className="bios-sim-monitor" role="group" aria-label={`Training computer screen: ${screen}`}>
        {screen === 'startup' ? (
          <div className="bios-sim-startup">
            <div className="bios-sim-msi-mark">MSI</div>
            <p className="bios-sim-board">PRO B650M-P · Training Workstation</p>
            <p className="bios-sim-post-line">Press DEL to enter Setup · Press F11 for Boot Menu</p>
            <div className="bios-sim-startup-controls" aria-label="Startup key choices">
              {control('startup_delete', 'DEL · Setup', 'bios-sim-key')}
              {control('startup_f11', 'F11 · Boot Menu', 'bios-sim-key')}
              {control('startup_wait', 'Wait', 'bios-sim-key bios-sim-key-muted')}
            </div>
          </div>
        ) : null}

        {screen === 'boot_menu' ? (
          <div className="bios-sim-boot-menu">
            <p className="bios-sim-kicker">MSI Boot Menu</p>
            <h3>Choose a one-time boot device</h3>
            <div className="bios-sim-menu-row bios-sim-menu-row-selected">Windows Boot Manager (NVMe SSD)</div>
            <p className="bios-sim-help-text">You opened the boot menu, not BIOS Setup.</p>
            {control('restart', 'Restart and try again', 'bios-sim-button bios-sim-button-primary')}
          </div>
        ) : null}

        {screen === 'windows' ? (
          <div className="bios-sim-windows">
            <div className="bios-sim-windows-logo" aria-hidden="true">⊞</div>
            <h3>Windows loaded</h3>
            <p>The startup window has passed. Restart to practice entering BIOS.</p>
            {control('restart', 'Restart computer', 'bios-sim-button bios-sim-button-primary')}
          </div>
        ) : null}

        {screen === 'bios' ? (
          <div className="bios-sim-bios">
            <header className="bios-sim-header">
              <div>
                <p className="bios-sim-kicker">MSI Click BIOS · Training View</p>
                <h3>Hardware Evidence Mission</h3>
              </div>
              <div className="bios-sim-mode">UEFI</div>
            </header>

            <nav className="bios-sim-tabs" aria-label="BIOS information sections">
              {control('bios_overview', 'Overview', 'bios-sim-tab', biosPage === 'overview')}
              {control('storage_tab', 'Storage', 'bios-sim-tab', biosPage === 'storage')}
              {control('boot_tab', 'Boot', 'bios-sim-tab', biosPage === 'boot')}
            </nav>

            {biosPage === 'overview' ? (
              <div className="bios-sim-grid">
                <button
                  type="button"
                  className={`bios-sim-card ${checks.cpu ? 'is-checked' : ''}`}
                  onClick={() => run('cpu_info')}
                  disabled={disabled || !hotspots.cpu_info}
                  aria-label={hotspots.cpu_info?.label || 'Inspect CPU information'}
                >
                  <span>CPU</span>
                  <strong>AMD Ryzen Processor</strong>
                  <small>{checks.cpu ? 'Evidence captured' : 'Inspect'}</small>
                </button>
                <button
                  type="button"
                  className={`bios-sim-card ${checks.memory ? 'is-checked' : ''}`}
                  onClick={() => run('memory_info')}
                  disabled={disabled || !hotspots.memory_info}
                  aria-label={hotspots.memory_info?.label || 'Inspect memory information'}
                >
                  <span>Memory</span>
                  <strong>16 GB DDR5</strong>
                  <small>{checks.memory ? 'Evidence captured' : 'Inspect'}</small>
                </button>
              </div>
            ) : null}

            {biosPage === 'storage' ? (
              <div className="bios-sim-grid bios-sim-grid-single">
                <button
                  type="button"
                  className={`bios-sim-card ${checks.nvme ? 'is-checked' : ''}`}
                  onClick={() => run('nvme_info')}
                  disabled={disabled || !hotspots.nvme_info}
                  aria-label={hotspots.nvme_info?.label || 'Inspect M.2 NVMe storage'}
                >
                  <span>M.2 / NVMe</span>
                  <strong>1 TB NVMe SSD detected</strong>
                  <small>{checks.nvme ? 'Evidence captured' : 'Inspect device'}</small>
                </button>
                <div className="bios-sim-note">
                  Classroom configuration: no SATA drives and no CD/DVD optical drive.
                </div>
              </div>
            ) : null}

            {biosPage === 'boot' ? (
              <div className="bios-sim-grid bios-sim-grid-single">
                <button
                  type="button"
                  className={`bios-sim-card ${checks.boot ? 'is-checked' : ''}`}
                  onClick={() => run('boot_manager')}
                  disabled={disabled || !hotspots.boot_manager}
                  aria-label={hotspots.boot_manager?.label || 'Inspect Windows Boot Manager'}
                >
                  <span>UEFI Boot Target</span>
                  <strong>Windows Boot Manager</strong>
                  <small>{checks.boot ? 'Evidence captured' : 'Inspect boot target'}</small>
                </button>
              </div>
            ) : null}

            <div className="bios-sim-progress" aria-label="Required BIOS evidence progress">
              <span className={checks.cpu ? 'is-complete' : ''}>CPU</span>
              <span className={checks.memory ? 'is-complete' : ''}>RAM</span>
              <span className={checks.nvme ? 'is-complete' : ''}>NVMe</span>
              <span className={checks.boot ? 'is-complete' : ''}>Boot</span>
            </div>

            <div className="bios-sim-restricted" aria-label="Configuration controls not authorized for this mission">
              <p>Configuration controls — observe the boundary</p>
              <div>
                {control('expo_control', 'EXPO', 'bios-sim-button bios-sim-button-warning')}
                {control('mflash_control', 'M-Flash', 'bios-sim-button bios-sim-button-warning')}
                {control('voltage_control', 'CPU Voltage', 'bios-sim-button bios-sim-button-warning')}
                {control('save_changes', 'Save Changes', 'bios-sim-button bios-sim-button-warning')}
              </div>
            </div>

            <footer className="bios-sim-footer">
              <span>Mission: gather evidence. Do not configure.</span>
              {control('safe_exit', 'Exit Without Saving', 'bios-sim-button bios-sim-button-primary')}
            </footer>
          </div>
        ) : null}
      </div>
    </div>
  );
};
